import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';
import { ClerkProvider } from './src/auth/ClerkProvider';
import { StageryRouter } from './src/routes';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initAnalytics } from './services/analytics';
import './src/index.css';

const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env ?? {};
const SENTRY_DSN = env.VITE_SENTRY_DSN ?? '';
const GIT_SHA = env.VITE_GIT_SHA ?? 'unknown';
const DEPLOY_ENV = env.VITE_DEPLOY_ENV ?? 'development';

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: GIT_SHA,
    environment: DEPLOY_ENV,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.01,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    beforeSend(event, hint) {
      // Redact PII from request bodies.
      if (event.request?.data && typeof event.request.data === 'object') {
        const d = event.request.data as Record<string, unknown>;
        if ('email' in d) d['email'] = '[redacted]';
        if ('phone' in d) d['phone'] = '[redacted]';
      }

      // STAGERY-FRONTEND-3: drop generic "Failed to fetch" / "Load failed" TypeErrors
      // — these are user-connectivity events, not application bugs. Also catches
      // ClerkJS-wrapped errors whose message *contains* these strings.
      const err = hint?.originalException;
      const errMsg = err instanceof Error ? err.message : '';
      if (
        err instanceof TypeError &&
        (errMsg === 'Failed to fetch' ||
          errMsg === 'NetworkError when attempting to fetch resource.' ||
          errMsg === 'Load failed' ||
          errMsg.includes('Load failed') ||
          errMsg.includes('Failed to fetch'))
      ) {
        return null;
      }

      // STAGERY-FRONTEND-5: drop WebAuthn/passkey NotSupportedError — Clerk v6
      // probes for passkey/resident-credential support on every sign-in page
      // load. HeadlessChrome (CI/bots) and some desktop browsers reject the
      // navigator.credentials.get() call with NotSupportedError when resident
      // credentials or an empty allowCredentials list are not supported. Clerk
      // handles the degradation internally (passkey UI is hidden); the unhandled
      // rejection is noise with no user impact or actionable signal.
      if (
        errMsg.includes('NotSupportedError') ||
        errMsg.includes('Resident credentials') ||
        errMsg.includes("allowCredentials' lists are not supported")
      ) {
        return null;
      }

      // STAGERY-FRONTEND-2: drop transient Clerk network errors on any
      // prepare_first_factor, prepare_second_factor, or prepare_verification
      // endpoint — these are CDN/mobile blips (WebKit aborts the in-flight
      // request when the page re-renders or the user navigates). Clerk retries
      // internally; surfacing these to Sentry is noise without actionable signal.
      const url =
        typeof event.request?.url === 'string' ? event.request.url : '';
      const breadcrumbUrls = (event.breadcrumbs ?? [])
        .map((b) => (b as { data?: { url?: string } }).data?.url ?? '')
        .join(' ');
      const isClerkPrepareEndpoint =
        url.includes('prepare_first_factor') ||
        url.includes('prepare_second_factor') ||
        url.includes('prepare_verification') ||
        breadcrumbUrls.includes('prepare_first_factor') ||
        breadcrumbUrls.includes('prepare_second_factor') ||
        breadcrumbUrls.includes('prepare_verification') ||
        errMsg.includes('prepare_verification');
      if (
        isClerkPrepareEndpoint &&
        event.exception?.values?.some(
          (v) =>
            v.type === 'TypeError' ||
            (typeof v.value === 'string' && v.value.toLowerCase().includes('network'))
        )
      ) {
        return null;
      }

      return event;
    },
  });
}

// Analytics — GA4 + GTM, gated op env-vars. No-op buiten productie en wanneer
// vars niet gezet zijn (zie services/analytics.ts).
if (DEPLOY_ENV !== 'development') {
  initAnalytics();
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ClerkProvider>
        <StageryRouter />
        <Toaster position="bottom-right" richColors />
      </ClerkProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
